import { supabaseAdmin } from '../lib/supabase';

export const roleVerificationService = {
  /**
   * Get current user's role data from all sources
   */
  async getCurrentUserVerification(userId) {
    try {
      const results = {
        authUsers: null,
        userProfile: null,
        jwtMetadata: null,
        syncStatus: {
          isConsistent: false,
          discrepancies: [],
          lastChecked: new Date()?.toISOString()
        }
      };

      // 1. Get auth.users data from current session
      const { data: { session }, error: sessionError } = await supabaseAdmin?.auth?.getSession();
      
      if (sessionError) {
        console.error('Session fetch error:', sessionError);
        results?.syncStatus?.discrepancies?.push('Failed to fetch session data');
      } else if (session?.user) {
        const authUser = session?.user;
        results.authUsers = {
          id: authUser?.id,
          email: authUser?.email,
          created_at: authUser?.created_at,
          updated_at: authUser?.updated_at,
          last_sign_in_at: authUser?.last_sign_in_at,
          raw_user_meta_data: authUser?.user_metadata,
          raw_app_meta_data: authUser?.app_metadata
        };

        // Extract JWT metadata
        results.jwtMetadata = {
          role: authUser?.user_metadata?.role || null,
          full_name: authUser?.user_metadata?.full_name || null,
          email: authUser?.email,
          claims: authUser?.app_metadata || {},
          aud: authUser?.aud,
          confirmed_at: authUser?.confirmed_at
        };
      } else {
        results?.syncStatus?.discrepancies?.push('No active session found');
      }

      // 2. Get user_profiles data
      const { data: profileData, error: profileError } = await supabaseAdmin?.from('user_profiles')?.select('*')?.eq('id', userId)?.maybeSingle();

      if (profileError) {
        console.error('User profile fetch error:', profileError);
        results?.syncStatus?.discrepancies?.push('Failed to fetch user_profiles data');
      } else {
        results.userProfile = profileData;
      }

      // 3. Check consistency
      if (results?.authUsers && results?.userProfile && results?.jwtMetadata) {
        const jwtRole = results?.jwtMetadata?.role;
        const profileRole = results?.userProfile?.role;
        const jwtEmail = results?.jwtMetadata?.email;
        const profileEmail = results?.userProfile?.email;

        if (jwtRole !== profileRole) {
          results?.syncStatus?.discrepancies?.push(
            `Role mismatch: JWT has '${jwtRole}' but user_profiles has '${profileRole}'`
          );
        }

        if (jwtEmail !== profileEmail) {
          results?.syncStatus?.discrepancies?.push(
            `Email mismatch: JWT has '${jwtEmail}' but user_profiles has '${profileEmail}'`
          );
        }

        results.syncStatus.isConsistent = results?.syncStatus?.discrepancies?.length === 0;
      } else {
        results?.syncStatus?.discrepancies?.push('Incomplete data from one or more sources');
      }

      return results;
    } catch (error) {
      console.error('Role verification error:', error);
      throw error;
    }
  },

  /**
   * Get all admin users for system-wide verification
   */
  async getAllAdminUsers() {
    try {
      const { data, error } = await supabaseAdmin?.from('user_profiles')?.select('id, email, full_name, role, created_at, updated_at')?.eq('role', 'admin')?.order('created_at', { ascending: false });

      if (error) throw error;
      return data || [];
    } catch (error) {
      console.error('Admin users fetch error:', error);
      throw error;
    }
  },

  /**
   * Get users with role discrepancies
   */
  async getUsersWithDiscrepancies() {
    try {
      // This would require a more complex query or multiple checks
      // For now, return empty array as placeholder
      return [];
    } catch (error) {
      console.error('Discrepancy check error:', error);
      throw error;
    }
  },

  /**
   * Trigger manual sync (placeholder for future implementation)
   */
  async triggerManualSync(userId) {
    try {
      // This would trigger a sync operation
      // For now, just return the current verification data
      return await this.getCurrentUserVerification(userId);
    } catch (error) {
      console.error('Manual sync error:', error);
      throw error;
    }
  }
};